import type { ApiClient } from '@tadpoles/api-client'

import type { AuthenticationService } from './authentication/authentication.svelte.js'
import type { AppLanguage } from './family-locale.svelte.js'
import type { UserScopedServicesManager } from './user-scoped-services-manager.svelte.js'

export type AccountLanguageSaveResult = 'aborted' | 'saved'

export class AccountLanguageService {
    readonly #apiClient: ApiClient
    readonly #auth: AuthenticationService
    readonly #userScopedServicesManager: UserScopedServicesManager
    #saveController: AbortController | undefined

    public constructor(
        apiClient: ApiClient,
        auth: AuthenticationService,
        userScopedServicesManager: UserScopedServicesManager
    ) {
        this.#apiClient = apiClient
        this.#auth = auth
        this.#userScopedServicesManager = userScopedServicesManager
    }

    public abortSave(): void {
        this.#saveController?.abort()
    }

    public async save(language: AppLanguage): Promise<AccountLanguageSaveResult> {
        const authState = this.#auth.state
        const managerState = this.#userScopedServicesManager.state

        if (authState.phase !== 'authenticated' || managerState.phase !== 'available') {
            throw new Error('App Language requires an authenticated user context.')
        }

        const personKey = managerState.services.context.person.key
        const saveController = new AbortController()
        this.#saveController = saveController

        try {
            await this.#apiClient.person.updateInfo({ language }, { signal: saveController.signal })

            if (saveController.signal.aborted) {
                return 'aborted'
            }

            if (
                !(await this.#userScopedServicesManager.reconcileLanguageUpdate(
                    authState.sessionContext,
                    personKey,
                    language,
                    saveController.signal
                ))
            ) {
                throw new Error('The App Language save completed for an inactive user context.')
            }

            return 'saved'
        } catch (error) {
            if (saveController.signal.aborted) {
                return 'aborted'
            }

            throw error
        } finally {
            if (this.#saveController === saveController) {
                this.#saveController = undefined
            }
        }
    }
}
